import React, { useState, useEffect, useRef } from "react"
import * as THREE from "three"
import { Canvas, useLoader, useFrame } from "react-three-fiber"
import { Billboard, PerspectiveCamera } from "@react-three/drei"

import { Z_STEP_SIZE } from "../config"

import images from "../content/round_7_images"

import { useSpring } from "react-spring"

import "./WebGLComposition.css"

const makeNewUrl = (path) => `//${window.location.host}/${path}`

function Artwork() {
	return false
}

export function TextureLoader({ elements = [], children, onLoad }) {
	const urls = images.map((src) => makeNewUrl(src.file))

	const textures = useLoader(THREE.TextureLoader, urls)
	const [isLoaded, setLoaded] = useState(false)

	useEffect(() => {
		if (!isLoaded && textures.length > 0) {
			setLoaded(true)
			onLoad && onLoad()
		}
	}, [isLoaded, onLoad, textures])

	if (textures.length === 0) {
		return false
	} else {
		textures.forEach((tex) => {
			if (tex.image) {
				const heightAspect = tex.image.naturalHeight / tex.image.naturalWidth
				tex.heightAspect = heightAspect
			}
		})
		return children({ textures })
	}
}

function DonkeyCam({ position, ...rest }) {
	const cameraRef = useRef()

	const { z } = useSpring({
		config: { mass: 1, friction: 50 },
		z: position[2],
	})

	useFrame((state) => {
		cameraRef.current.position.x =
			position[0] + Math.sin(state.clock.elapsedTime / 4) * 14
		cameraRef.current.position.y =
			position[1] + Math.sin(state.clock.elapsedTime / 2) * 2
		cameraRef.current.position.z = z.value
	})

	return (
		<PerspectiveCamera
			ref={cameraRef}
			makeDefault
			fov={35}
			rotation={[Math.PI * -0.05, 0, 0]}
			zoom={window.innerWidth > 640 ? 1.25 : 0.66}
			{...rest}
		/>
	)
}

export function WebGLComposition({
	elements = [],
	textures = [],
	seed,
	className,
}) {
	const whatevRef = useRef()

	useEffect(() => {
		if (whatevRef.current) {
			whatevRef.current.outputEncoding = THREE.sRGBEncoding
		}
	}, [whatevRef])

	const [pos, setPos] = useState({ x: 0, y: 0 })

	useEffect(() => {
		window.addEventListener("mousemove", (e) => {
			const xPos = e.clientX / window.innerWidth
			const yPos = e.clientY / window.innerHeight
			setPos({ x: xPos * 2 - 1, y: yPos * 2 - 1 })
		})
	}, [])

	const cameraPosition = [
		0 + pos.x * 30,
		100 - pos.y * 10,
		300 + seed.length * Z_STEP_SIZE,
	]

	return (
		<div className={`WebGLComposition ${className}`}>
			<Canvas
				colorManagement={false}
				style={{ height: "75vh" }}
				gl={{ precision: "lowp", antialias: true, premultipliedAlpha: true }}
			>
				<fog attach="fog" args={["#00cccc", 600, 1200]} />

				<DonkeyCam position={cameraPosition} />

				{textures.length > 0 &&
					elements.map((el, i) => (
						<Billboard
							scale={[el.scaleX / 100, el.scaleY / 100, 100.0 / 100]}
							key={`bb-${el.key}`}
							position={[
								parseFloat(el.x),
								parseFloat(el.y),
								el.o * Z_STEP_SIZE,
							]}
							args={[1 * 100, textures[el.imgIndex].heightAspect * 100]}
							rotation={[0, 0, parseFloat(el.rotation)]}
							follow={false}
						>
							<meshBasicMaterial
								map={textures[el.imgIndex]}
								alphaTest={0.1}
								transparent={true}
								precision="lowp"
							/>
						</Billboard>
					))}

				<Artwork elements={elements} />
			</Canvas>
		</div>
	)
}
